import { useEffect, useState } from 'react';
import type { Formulation } from '../lib/types';
import { toDateInputValue, parseNumber, toDate } from '../lib/format';
import {
  createFormulation,
  emptyDraft,
  updateFormulationMeta,
} from '../services/formulations';
import type { FormulationDraft } from '../services/formulations';
import { Button, Input, Modal, Textarea } from './ui';


export default function FormulationDialog({
  projectId,
  target,
  onClose,
}: {
  projectId: string;
  target: Formulation | 'new' | null;
  onClose: () => void;
}) {
  const isNew = target === 'new';
  const existing = isNew ? null : target;
  const [draft, setDraft] = useState<FormulationDraft>(emptyDraft);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!target) return;
    setDraft(
      existing
        ? {
            label: existing.label ?? '',
            lipid: existing.lipid ?? '',
            np: existing.np ?? null,
            modification: existing.modification ?? '',
            measuredAt: toDate(existing.measuredAt),
            notes: existing.notes ?? '',
          }
        : emptyDraft(),
    );
  }, [target, existing]);

  const set = <K extends keyof FormulationDraft>(key: K, value: FormulationDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  async function save() {
    if (!draft.label.trim()) return;
    setBusy(true);
    try {
      if (isNew) await createFormulation(projectId, draft);
      else if (existing) await updateFormulationMeta(projectId, existing.id, draft);
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={target !== null}
      onClose={onClose}
      title={isNew ? 'New sample' : 'Edit sample'}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
        className="space-y-4"
      >
        <div>
          <label className="field-label mb-1.5" htmlFor="f-label">
            Label
          </label>
          <Input
            id="f-label"
            autoFocus
            value={draft.label}
            onChange={(e) => set('label', e.target.value)}
            placeholder="Sample label"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="field-label mb-1.5" htmlFor="f-lipid">
              Material
            </label>
            <Input
              id="f-lipid"
              value={draft.lipid}
              onChange={(e) => set('lipid', e.target.value)}
              placeholder="Material"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="field-label mb-1.5" htmlFor="f-np">
              N/P
            </label>
            <Input
              id="f-np"
              type="number"
              inputMode="decimal"
              step="any"
              value={draft.np ?? ''}
              onChange={(e) => set('np', parseNumber(e.target.value))}
              placeholder=""
            />
          </div>
        </div>

        {/* Directly after N/P, matching the table and the export, because it is
            read as part of "how does this one differ" rather than as a note. */}
        <div>
          <label className="field-label mb-1.5" htmlFor="f-modification">
            Modification
          </label>
          <Input
            id="f-modification"
            value={draft.modification}
            onChange={(e) => set('modification', e.target.value)}
            placeholder="What was changed for this sample"
          />
        </div>

        <div>
          <label className="field-label mb-1.5" htmlFor="f-date">
            Date measured
          </label>
          <Input
            id="f-date"
            type="date"
            value={toDateInputValue(draft.measuredAt)}
            onChange={(e) =>
              set('measuredAt', e.target.value ? new Date(`${e.target.value}T12:00:00`) : null)
            }
          />
        </div>

        <div>
          <label className="field-label mb-1.5" htmlFor="f-notes">
            Notes
          </label>
          <Textarea
            id="f-notes"
            rows={2}
            value={draft.notes}
            onChange={(e) => set('notes', e.target.value)}
            placeholder="Optional"
          />
        </div>

        <p className="text-xs text-ink-400">
          Measurements are added separately — by photo, voice or keyboard.
        </p>

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={busy} disabled={!draft.label.trim()}>
            {isNew ? 'Create' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
